import { IonButton, IonButtons, IonContent, IonHeader, IonModal, IonToolbar } from '@ionic/react';
import { useMemo } from 'react';
import type { Recipe } from '../../domain/recipe';
import { recipeName } from '../i18n/format';
import { useMessages } from '../i18n/LanguageProvider';
import { useAppData } from '../state/AppDataProvider';
import { usePlanEdits } from '../state/use-plan-edits';
import { quickChoices, swapNotice } from './no-energy';

/**
 * DE: Liste schneller Gerichte; ein Tipp wählt eines aus.
 * EN: List of quick dinners; a tap picks one.
 */
export function AlternativesList({
  recipes,
  onChoose,
  detail,
}: {
  recipes: readonly Recipe[];
  onChoose: (recipeId: string) => void;
  detail?: (recipe: Recipe) => string;
}) {
  const { language, t } = useMessages();
  const describe = detail ?? ((recipe: Recipe) => t.minutes(recipe.totalMinutes));
  if (recipes.length === 0) return <p className="app-muted">{t.noAlternatives}</p>;
  return (
    <ul className="app-rows">
      {recipes.map((recipe) => (
        <li key={recipe.id}>
          <button type="button" className="app-row" onClick={() => onChoose(recipe.id)}>
            <span>{recipeName(recipe, language)}</span>
            <span className="app-muted app-row-value">{describe(recipe)}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

/**
 * DE: „Keine Kraft“: schnelles Gericht wählen; das geplante wandert auf einen späteren Abend.
 * EN: "No energy": pick a quick dinner; the planned one moves to a later evening.
 */
export function QuickSwap({ onDone }: { onDone: (notice: string | undefined) => void }) {
  const { data, today } = useAppData();
  const plans = usePlanEdits();
  const { language, t } = useMessages();
  const recipes = useMemo(() => quickChoices(data, today), [data, today]);
  const choose = (recipeId: string) => {
    void plans
      .edit({ kind: 'no-energy', recipeId })
      .then((result) => onDone(swapNotice(result.notice, language)));
  };
  return (
    <>
      <p className="app-lead">{t.noEnergyTitle}</p>
      <p className="app-muted">{t.noEnergyHint}</p>
      <AlternativesList recipes={recipes} onChoose={choose} />
    </>
  );
}

/**
 * DE: „Keine Kraft“ als Blatt über dem Bildschirm „Heute“.
 * EN: "No energy" as a sheet above the Today screen.
 */
export function NoEnergySheet({
  isOpen,
  onClose,
  onNotice,
}: {
  isOpen: boolean;
  onClose: () => void;
  onNotice: (text: string) => void;
}) {
  const { t } = useMessages();
  const done = (notice: string | undefined) => {
    if (notice) onNotice(notice);
    onClose();
  };
  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={onClose}
      initialBreakpoint={0.75}
      breakpoints={[0, 0.75, 1]}
    >
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="end">
            <IonButton onClick={onClose}>{t.close}</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">{isOpen && <QuickSwap onDone={done} />}</IonContent>
    </IonModal>
  );
}
